import React from "react";
import {
  DefaultButton,
  DetailsList,
  DetailsListLayoutMode,
  Dialog,
  DialogFooter,
  DialogType,
  PrimaryButton,
  SelectionMode,
} from "@fluentui/react";

// Parse the error string format, error example: "Proprietary general Amount must not be greater than the total available. ActivityPointId=18 Cobrand=19 Topic= AmountAllowed=0 Amount=3"
export const parseErrorString = (errorString) => {
  if (!errorString || typeof errorString !== "string") return [];

  const errors = errorString.split(";").filter((s) => s.trim());
  return errors.map((error) => {
    const parsed = {
      errorMessage: error.trim(),
      cobrandId: null,
      cobrandName: null,
      actionItemId: null,
      errorType: "ValidationError",
      amountRequired: null,
      amountAvailable: null,
    };

    // Extract Cobrand ID
    const cobrandMatch = error.match(/Cobrand=(\d+)/);
    if (cobrandMatch) {
      parsed.cobrandId = parseInt(cobrandMatch[1], 10);
      parsed.cobrandName = `Cobrand ${cobrandMatch[1]}`;
    }

    // Extract ActivityPointId (which relates to action item type)
    const pointsMatch = error.match(/ActivityPointId=(\d+)/);
    if (pointsMatch) {
      parsed.actionItemId = `PointsID: ${pointsMatch[1]}`;
    }

    // Extract Course ID if present
    const courseMatch = error.match(/Course=(\d+)/);
    if (courseMatch) {
      parsed.actionItemId = `Course: ${courseMatch[1]}`;
    }

    // Extract amounts
    const amountAllowedMatch = error.match(/AmountAllowed=(\d+)/);
    if (amountAllowedMatch) {
      parsed.amountAvailable = parseInt(amountAllowedMatch[1], 10);
    }

    const amountMatch = error.match(/Amount=(\d+)/);
    if (amountMatch) {
      parsed.amountRequired = parseInt(amountMatch[1], 10);
    }

    // Determine error type
    if (error.includes("Proprietary general Amount")) {
      parsed.errorType = "InsufficientContent";
    } else if (error.includes("specific course does not exist")) {
      parsed.errorType = "MissingCourse";
    }

    return parsed;
  });
};

const ValidationErrorsModal = ({
  isOpen,
  onDismiss,
  validationErrors,
  onExcludeCobrands,
  isGlobalOn,
  cobrandOptions,
}) => {
  // Map cobrand IDs to names using the options
  const getCobrandName = (cobrandId) => {
    if (!cobrandId) return "Unknown";
    const cobrand = cobrandOptions?.find((c) => c.value === cobrandId);
    return cobrand?.label || `Cobrand ${cobrandId}`;
  };

  // validation errors with cobrand names
  const parsedErrors = validationErrors.map((e) => ({
    ...e,
    cobrandName: getCobrandName(e.cobrandId),
  }));

  const columns = [
    {
      key: "cobrandName",
      name: "Cobrand",
      fieldName: "cobrandName",
      minWidth: 150,
      maxWidth: 250,
    },
    {
      key: "actionItemId",
      name: "Action Item ID",
      fieldName: "actionItemId",
      minWidth: 80,
      maxWidth: 100,
    },
    {
      key: "errorType",
      name: "Error Type",
      fieldName: "errorType",
      minWidth: 100,
      maxWidth: 150,
    },
    {
      key: "errorMessage",
      name: "Details",
      fieldName: "errorMessage",
      minWidth: 200,
      maxWidth: 400,
    },
  ];

  // Get unique cobrand IDs that have errors
  const erroringCobrandIds = [
    ...new Set(parsedErrors.filter((e) => e.cobrandId).map((e) => e.cobrandId)),
  ];

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={onDismiss}
      dialogContentProps={{
        type: DialogType.largeHeader,
        title: "Checklist Assignment Validation Errors",
        subText: `The following ${erroringCobrandIds.length} cobrand(s) cannot support this checklist's action items. You can exclude them from the assignment and save again.`,
      }}
      modalProps={{
        isBlocking: true,
        styles: { main: { width: '100% !important', maxWidth: '1100px !important' } },
      }}
    >
      <DetailsList
        items={parsedErrors}
        columns={columns}
        selectionMode={SelectionMode.none}
        layoutMode={DetailsListLayoutMode.justified}
        isHeaderVisible={true}
      />
      <DialogFooter>
        <PrimaryButton
          onClick={() => onExcludeCobrands(erroringCobrandIds)}
          text={
            isGlobalOn
              ? `Add ${erroringCobrandIds.length} cobrand(s) to exclude list`
              : `Remove ${erroringCobrandIds.length} cobrand(s) from include list`
          }
        />
        <DefaultButton onClick={onDismiss} text="Cancel" />
      </DialogFooter>
    </Dialog>
  );
};

export default ValidationErrorsModal;
